import { ThemeOptions } from '@mui/material/styles';
import { Theme } from '@mui/material/styles';
import { tokens } from '../tokens/tokens';
import { media } from './breakpoints';

export const buildComponentsTheme = (
    bp: Theme
): ThemeOptions => ({
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    fontFamily: tokens.fontFamily.sans,
                },
                'code, pre, kbd, [data-monospace]': {
                    fontFamily: tokens.fontFamily.mono,
                },
            },
        },

        MuiCircularProgress: {
            styleOverrides: {
                root: ({ theme }) => ({
                    color: theme.palette.primary.main,
                }),
            }
        },

        MuiDrawer: {
            defaultProps: { anchor: 'left' },
            styleOverrides: {
                root: ({ ownerState }) => ({
                    ...(ownerState.variant === 'permanent' && {
                        width: ownerState.open ? 290 : 70,
                        flexShrink: 0,
                        transition: 'width 0.3s ease',
                        zIndex: 10,
                    }),
                }),
                paper: ({ ownerState, theme }) => ({
                    backgroundColor: theme.palette.layout.sidebar,
                    borderRight: `1px solid ${theme.palette.dividers.subtle}`,
                    overflowX: 'hidden',
                    zIndex: 10,

                    ...(ownerState.variant === 'permanent' && {
                        width: ownerState.open ? '290px' : '70px',
                        padding: ownerState.open ? '24px 24px 16px' : '24px 11px 16px',
                        transition: 'width 0.3s ease, padding 0.3s ease',
                    }),

                    ...(ownerState.variant === 'temporary' && {
                        width: '290px',
                        padding: '24px 24px 16px',
                    }),
                }),
            },
        },

        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: tokens.radius.xl,
                }
            }
        },

        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontSize: tokens.fontSize.lg,
                }
            }
        },

        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: '0 32px 16px 32px',
                }
            }
        },

        MuiButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: tokens.radius.xl,
                    textTransform: 'none',
                    boxShadow: 'none',
                    padding: '8px 12px',
                    fontWeight: tokens.fontWeight.medium,
                    transition: 'all 0.2s ease-in-out',

                    letterSpacing: tokens.letterSpacing.button,
                    cursor: 'pointer',

                    '&:disabled': {
                        pointerEvents: 'none',
                    },

                    variants: [
                        {
                            props: { variant: 'contained', color: 'primary' },
                            style: {
                                backgroundColor:        theme.buttonTokens.primary.bg,
                                color:                  theme.buttonTokens.primary.color,
                                border:                 `1px solid ${theme.buttonTokens.primary.border}`,

                                '&:hover': {
                                    backgroundColor:    theme.buttonTokens.primary.hoverBg,
                                    border:             `1px solid ${theme.buttonTokens.primary.hoverBorder}`,
                                },
                                '&:active': {
                                    backgroundColor:    theme.buttonTokens.primary.activeBg,
                                    border:             `1px solid ${theme.buttonTokens.primary.activeBorder}`,
                                },
                                '&:focus-visible': {
                                    boxShadow:          `0 0 0 3px ${theme.buttonTokens.primary.focusRingShadow}`,
                                },
                                '&:disabled': {
                                    backgroundColor:    theme.buttonTokens.primary.disabledBg,
                                    color:              theme.buttonTokens.primary.disabledColor,
                                    border:             `1px solid ${theme.buttonTokens.primary.disabledBorder}`,
                                },
                                '& .MuiCircularProgress-root': {
                                    color: theme.buttonTokens.primary.color,
                                },
                            }
                        },
                        {
                            props: { variant: 'outlined', color: 'primary' },
                            style: {
                                backgroundColor:        theme.buttonTokens.secondary.bg,
                                color:                  theme.buttonTokens.secondary.color,
                                border:                 `1px solid ${theme.buttonTokens.secondary.border}`,

                                '&:hover': {
                                    backgroundColor:    theme.buttonTokens.secondary.hoverBg,
                                    color:              theme.buttonTokens.secondary.hoverColor,
                                    border:             `1px solid ${theme.buttonTokens.secondary.hoverBorder}`,
                                },
                                '&:active': {
                                    backgroundColor:    theme.buttonTokens.secondary.activeBg,
                                    color:              theme.buttonTokens.secondary.activeColor,
                                },
                                '&:focus-visible': {
                                    boxShadow:          `0 0 0 3px ${theme.buttonTokens.secondary.focusRingShadow}`,
                                },
                                '&:disabled': {
                                    backgroundColor:    theme.buttonTokens.secondary.disabledBg,
                                    color:              theme.buttonTokens.secondary.disabledColor,
                                    border:             `1px solid ${theme.buttonTokens.secondary.disabledBorder}`,
                                },
                                '& .MuiCircularProgress-root': {
                                    color:              theme.buttonTokens.secondary.color,
                                },
                            }
                        },
                        {
                            props: { variant: 'text', color: 'primary' },
                            style: {
                                backgroundColor:    theme.buttonTokens.ternary.bg,
                                color:              theme.buttonTokens.ternary.color,

                                '&:hover': {
                                    backgroundColor: theme.buttonTokens.ternary.hoverBg,
                                    color:           theme.buttonTokens.ternary.hoverColor,
                                },
                                '&:active': {
                                    backgroundColor: theme.buttonTokens.ternary.activeBg,
                                },
                                '&:focus-visible': {
                                    boxShadow:      `0 0 0 3px ${theme.buttonTokens.ternary.focusRingShadow}`,
                                },
                                '&:disabled': {
                                    color:          theme.buttonTokens.ternary.disabledColor,
                                },
                            },
                        },
                        {
                            props: { variant: 'link' },
                            style: {
                                backgroundColor: 'transparent',
                                color:           theme.buttonTokens.link.color,
                                border:          'none',
                                padding:         '0',
                                minWidth:        'unset',
                                fontWeight:      tokens.fontWeight.medium,
                                transition:       'all 0.3s ease',

                                '&:hover': {
                                    backgroundColor: 'transparent',
                                    color:           theme.buttonTokens.link.hoverColor,
                                    textDecoration:  'underline',
                                },
                                '&:active': {
                                    backgroundColor: 'transparent',
                                    color:           theme.buttonTokens.link.activeColor,
                                },
                                '&:visited': {
                                    color:           theme.buttonTokens.link.visitedColor,
                                },
                                '&:focus-visible': {
                                    boxShadow:       `0 0 0 2px ${theme.buttonTokens.link.focusRing}`,
                                    borderRadius:    tokens.radius.xs,
                                    textDecoration:  'none',
                                },
                                '&:disabled': {
                                    color:           theme.buttonTokens.link.disabledColor,
                                    textDecoration:  'none',
                                    backgroundColor: 'transparent',
                                },
                            },
                        },
                    ]
                }),
            },
        },

        MuiTextField: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: tokens.radius.xl,

                    '&  .MuiOutlinedInput-root': {
                        borderRadius: tokens.radius.xl,
                        fontSize: tokens.fontSize.sm,
                        color: theme.buttonTokens.secondary.color,

                        '& .MuiInputBase-input': {
                            fontSize: tokens.fontSize.sm,
                        },

                        '& fieldset': {
                            borderColor: theme.palette.dividers.default,
                        },

                        '&:hover fieldset': {
                            borderColor: theme.palette.dividers.focus,
                        },

                        '&.Mui-focused fieldset': {
                            borderColor: theme.palette.dividers.focus,
                        },

                        '&.Mui-disabled fieldset': {
                            borderColor: theme.palette.dividers.default,
                        },

                        '& input.MuiInputBase-input::placeholder': {
                            fontSize: tokens.fontSize.sm,
                            color: theme.palette.text.placeholder,
                            opacity: 1,
                        },

                        '& textarea.MuiInputBase-input::placeholder': {
                            fontSize: tokens.fontSize.sm,
                            color: theme.palette.text.placeholder,
                            opacity: 1,
                        },
                    },

                    [media.smPlus]: {
                        '& .MuiOutlinedInput-root': {
                            fontSize: tokens.fontSize.base,

                            '& .MuiInputBase-input': {
                                fontSize: tokens.fontSize.base,
                            },

                            '& input.MuiInputBase-input::placeholder': {
                                fontSize: tokens.fontSize.base,
                            },

                            '& textarea.MuiInputBase-input::placeholder': {
                                fontSize: tokens.fontSize.base,
                            },
                        },
                    },
                }),
            },
        },

        MuiInputLabel: {
            styleOverrides: {
                root: ({ theme }) => ({
                    fontSize: tokens.fontSize.base,
                    color: theme.palette.text.hint,

                    '&&.Mui-focused': {
                        color: theme.palette.text.primary,
                    },

                    [bp.breakpoints.down('smPlus')]: {
                        fontSize: tokens.fontSize.sm,
                    },
                }),
            },
        },

        MuiSelect: {
            styleOverrides: {
                root: ({ theme }) => ({
                    borderRadius: tokens.radius.xl,
                    backgroundColor: theme.palette.background.paper,

                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.dividers.default,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.dividers.subtle,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.dividers.subtle,
                    },
                }),
            },
        },

        MuiTabs: {
            styleOverrides: {
                root: ({ theme }) => ({
                    minHeight: 40,
                    borderBottom: `1px solid ${theme.palette.dividers.light}`,
                }),
                indicator: ({ theme }) => ({
                    backgroundColor: theme.palette.primary.main,
                    height: '2px',
                }),
            },
        },

        MuiTab: {
            styleOverrides: {
                root: ({ theme }) => ({
                    minHeight: 40,
                    fontSize: tokens.fontSize.base,
                    fontWeight: tokens.fontWeight.normal,
                    textTransform: 'none',
                    letterSpacing: '0.4px',

                    color: theme.palette.text.primary,

                    '&.Mui-selected': {
                        color: theme.palette.text.primary,
                        fontWeight: tokens.fontWeight.medium,
                    },
                    '&:hover': {
                        color: theme.palette.text.primary,
                        opacity: 1,
                        background: theme.palette.background.dropzone,
                    },
                    '&.Mui-disabled': {
                        color: theme.palette.text.disabled,
                        opacity: 0.5,
                    },
                }),
            },
        },

        MuiDivider: {
            styleOverrides: {
                root: ({ ownerState, theme }) => ({
                    borderColor: theme.palette.dividers.default,
                    borderStyle: 'solid',

                    // tylko dla horyzontalnych
                    ...((!ownerState.orientation || ownerState.orientation === 'horizontal') && {
                        margin: '12px 0',
                        borderBottomWidth: '1px',
                        width: '100%',
                    }),

                    // tylko dla wertykalnych
                    ...(ownerState.orientation === 'vertical' && {
                        margin: '0 12px',
                        borderRightWidth: '1px',
                        width: 'auto',
                        height: 'auto',
                        alignSelf: 'stretch',
                    }),
                }),
            },
        },

        MuiMenu: {
            defaultProps: {
                elevation: 8,
            },
            styleOverrides: {
                paper: ({ theme }) => ({
                    boxShadow: tokens.shadow.lg,
                    borderRadius: tokens.radius.xs,
                    fontSize: tokens.fontSize.sm,
                }),
            },
        },

        MuiMenuItem: {
            styleOverrides: {
                root: tokens.fontSize.sm,
            },
        },

        MuiAlert: {
            styleOverrides: {
                root: ({ ownerState, theme }) => ({
                    borderRadius: tokens.radius.md,
                    width: '100%',
                    boxShadow: '0px 3px 8px rgba(0, 0, 0, 0.15)',

                    '& .MuiAlert-icon': {
                        fontSize: tokens.fontSize.lg,
                    },
                    '& .MuiAlert-message': {
                        fontSize: tokens.fontSize.sm,
                        fontWeight: tokens.fontWeight.medium,
                    },

                    ...(ownerState.severity === 'success' && {
                        backgroundColor: tokens.colors.green100,
                        color: tokens.colors.green900,
                        '& .MuiAlert-icon': { color: tokens.colors.green700 },
                    }),
                    ...(ownerState.severity === 'error' && {
                        backgroundColor: tokens.colors.red100,
                        color: tokens.colors.red900,
                        '& .MuiAlert-icon': { color: tokens.colors.red700 },
                    }),
                    ...(ownerState.severity === 'info' && {
                        backgroundColor: tokens.colors.blue100,
                        color: tokens.colors.blue900,
                        '& .MuiAlert-icon': { color: tokens.colors.blue700 },
                    }),
                    ...(ownerState.severity === 'warning' && {
                        backgroundColor: tokens.colors.amber100,
                        color: tokens.colors.amber900,
                        '& .MuiAlert-icon': { color: tokens.colors.amber700 },
                    }),
                }),
            },
        },

        MuiCheckbox: {
            defaultProps: {
                color: 'primary',
                disableRipple: true,
            },
            styleOverrides: {
                root: ({ theme }) => ({
                    width: '16px',
                    height: '16px',
                    padding: '8px 8px',
                    borderRadius: tokens.radius.sm,
                    fontSize: tokens.fontSize.sm,

                    '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                    },

                    '&.Mui-checked': {
                        color: theme.palette.primary.main,
                    },

                    '&.MuiCheckbox-indeterminate': {
                        color: theme.palette.primary.main,
                    },

                    '&.Mui-disabled': {
                        color: theme.palette.action.disabled,
                    },
                })
            }
        },

        MuiTooltip: {
            styleOverrides: {
                tooltip: ({ theme }) => ({
                    backgroundColor: theme.palette.secondary.main,
                    color: theme.palette.secondary.contrastText,
                }),
                arrow: ({ theme }) => ({
                    color: theme.palette.secondary.main,
                }),
            },
        },

        MuiToggleButton: {
            styleOverrides: {
                root: ({theme}) => ({
                    '&.Mui-disabled': {
                        color: theme.palette.text.hint,
                        backgroundColor: theme.palette.background.default,
                        borderColor: theme.palette.dividers.subtle,
                    }
                })
            }
        }
    },
});